import React, { useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { Button } from "components/Buttons/Button";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { icons } from "constants/icons";
import Styled from "./PaywallModal.style";
import { SUBSCRIPTION_PRICES } from "utils/subscription";
import {
  createStripeCheckoutSession,
  conciergeRequest,
} from "repositories/coachApi";
import { useAppSelector, useAppDispatch } from "hooks/reduxHooks";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { enqueueSnackbar } from "notistack";
import { getSubscriptionPlan } from "utils/subscription";
import { setCurrentClinician } from "store/ClinicianStore/clinicianSlice";
import { AuthRepository } from "repositories/AuthRepository";
import SuccessFailedModal from "components/SuccessFailedModal/SuccessFailedModal";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface AthleteAttempt {
  athleteId: string;
  athleteName: string;
  avatar?: string;
  attempts: number;
}

type PaywallOptions = "free" | "pro" | "concierge";

interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
  athleteAttempts?: AthleteAttempt[];
  totalAthletes?: number;
  onDowngrade: () => void;
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  open,
  onClose,
  athleteAttempts = [],
  onDowngrade,
  totalAthletes = 0,
}) => {
  const dispatch = useAppDispatch();
  const currentClinician = useAppSelector(selectCurrentClinician);
  const currentPlan = getSubscriptionPlan(
    Number(currentClinician?.subscription?.plan),
    currentClinician?.hasLifetimeAccess ?? false
  );
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PaywallOptions>(
    currentPlan.paywallDefaultOption as PaywallOptions
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleUpdatePlan = (tier: PaywallOptions) => {
    setSelectedTier(tier);
  };

  const getUpgradeButtonProps = () => {
    const isDowngrading =
      (selectedTier === "pro" && currentPlan.slug === "concierge") ||
      (selectedTier === "free" && currentPlan.slug !== "free");
    const defaultTitle = isDowngrading ? "Downgrade Now" : "Upgrade Now";
    const disabled = upgradeLoading || selectedTier === currentPlan.slug;
    const isConcierge = selectedTier === "concierge";
    return {
      title: isConcierge
        ? "I'm interested"
        : selectedTier === currentPlan.slug
        ? "Current Plan"
        : defaultTitle,
      colorScheme: disabled
        ? ButtonColorScheme.disabled
        : ButtonColorScheme.gradient,
      disabled,
    };
  };

  const handleStripeCheckout = async (
    tier: Exclude<PaywallOptions, "free">
  ) => {
    const price = SUBSCRIPTION_PRICES[tier];
    try {
      const response = await createStripeCheckoutSession(price);
      if (response.operation === "createCheckoutSession") {
        const url = response.data.url;
        window.location.href = url;
      }

      if (response.operation === "updateSubscription") {
        enqueueSnackbar("Subscription updated successfully", {
          variant: "success",
        });
        const authRepository = AuthRepository.getInstance();
        const updatedClinician = await authRepository.getMe();
        await sleep(3000);
        dispatch(setCurrentClinician(updatedClinician));
      }
    } catch (error: unknown) {
      const defaultMessage = "Failed to start checkout. Please try again.";
      const message = (() => {
        if (typeof error === "object" && error !== null && "message" in error) {
          const maybeMessage = (error as Record<string, unknown>).message;
          if (typeof maybeMessage === "string") return maybeMessage;
        }
        return defaultMessage;
      })();

      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleUpgrade = async (tier: PaywallOptions) => {
    setUpgradeLoading(true);
    try {
      if (tier === "concierge") {
        await conciergeRequest();
        setShowSuccessModal(true);
      } else if (tier === "pro") {
        await handleStripeCheckout(tier);
      } else {
        onDowngrade();
        // Free plan selected; downgrades are disabled via button state.
      }
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleConciergeSuccessClose = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: "600px",
          margin: "auto",
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <Styled>
          <div className="modal-container">
            {/* Header */}
            <div className="modal-header">
              <button className="close-button" onClick={onClose}>
                <img src={icons.modalClose} alt="close" />
              </button>
            </div>

            {/* Content */}
            <div className="modal-content">
              <h2 className="modal-title">Upgrade Subscription Plan</h2>

              {athleteAttempts.length > 0 && (
                <>
                  <p className="modal-subtitle">
                    {totalAthletes} athletes tried to use AI analysis this week
                    but hit their limit.
                  </p>

                  {/* Athletes List */}
                  <div className="athletes-list">
                    {athleteAttempts.map((athlete) => (
                      <div key={athlete.athleteId} className="athlete-item">
                        <div className="athlete-info">
                          <div className="athlete-avatar">
                            {athlete.avatar ? (
                              <img
                                src={athlete.avatar}
                                alt={athlete.athleteName}
                              />
                            ) : (
                              <span>{getInitials(athlete.athleteName)}</span>
                            )}
                          </div>
                          <span className="athlete-name">
                            {athlete.athleteName}
                          </span>
                        </div>
                        <span className="attempts-count">
                          {athlete.attempts} attempts
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Upgrade Plans */}
              <div className="upgrade-plans">
                {/* Tabs */}
                <div
                  className="plan-tabs"
                  role="tablist"
                  aria-label="Plan selector"
                >
                  <button
                    role="tab"
                    aria-selected={selectedTier === "free"}
                    className={`tab-button ${
                      selectedTier === "free" ? "active" : ""
                    }`}
                    onClick={() => handleUpdatePlan("free")}
                  >
                    Free Plan
                  </button>
                  <button
                    role="tab"
                    aria-selected={selectedTier === "pro"}
                    className={`tab-button ${
                      selectedTier === "pro" ? "active" : ""
                    }`}
                    onClick={() => handleUpdatePlan("pro")}
                  >
                    Allheartz Pro
                  </button>
                  <button
                    role="tab"
                    aria-selected={selectedTier === "concierge"}
                    className={`tab-button ${
                      selectedTier === "concierge" ? "active" : ""
                    }`}
                    onClick={() => handleUpdatePlan("concierge")}
                  >
                    Concierge Tier
                  </button>
                </div>
                {/* Selected plan card */}
                {selectedTier === "free" && (
                  <div className="plan-card pro-plan">
                    <h3 className="plan-title">
                      {currentPlan?.name === "Free Plan"
                        ? "Current Plan"
                        : "Switch to"}{" "}
                      : Free Plan
                    </h3>
                    <div className="plan-price">
                      $0<span className="per-month">/month</span>
                    </div>
                    <div className="plan-features">
                      <div className="feature-item">
                        <span className="feature-number">1</span>
                        <span className="feature-text">
                          Unlimited athletes, up to 5 free AI analyses.
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-number">2</span>
                        <span className="feature-text">
                          Advanced reporting tools
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-number">3</span>
                        <span className="feature-text">
                          Comparison features
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-number">4</span>
                        <span className="feature-text">
                          Treatment recommendations
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {selectedTier === "pro" && (
                  <div className="plan-card pro-plan">
                    <h3 className="plan-title">
                      {currentPlan?.name === "Allheartz Pro"
                        ? "Current Plan"
                        : "Upgrade to"}{" "}
                      : Allheartz Pro
                    </h3>
                    <div className="plan-price">
                      $100<span className="per-month">/month</span>
                    </div>
                    <div className="plan-features">
                      <div className="feature-item">
                        <span className="feature-number">1</span>
                        <span className="feature-text">
                          Unlimited athletes, up to 50 free AI analyses.
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-number">2</span>
                        <span className="feature-text">
                          Advanced reporting tools
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-number">3</span>
                        <span className="feature-text">
                          Comparison features
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-number">4</span>
                        <span className="feature-text">
                          Treatment recommendations
                        </span>
                      </div>
                    </div>
                  </div>
                )}{" "}
                {selectedTier === "concierge" && (
                  <div className="plan-card concierge-plan">
                    <h3 className="plan-title concierge-title">
                      {currentPlan?.name === "Concierge Tier"
                        ? "Current Plan"
                        : "Upgrade to"}{" "}
                      : Concierge Tier
                    </h3>
                    <div className="plan-price" style={{ color: "#fff" }}>
                      $3000
                      <span className="per-month" style={{ color: "#fff" }}>
                        /month
                      </span>
                    </div>
                    <div className="plan-features">
                      <div className="feature-item">
                        <span className="feature-checkmark">✓</span>
                        <span className="concierge-plan-feature-text">
                          All features from Pro Tier included
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-checkmark">✓</span>
                        <span className="concierge-plan-feature-text">
                          Dedicated virtual specialist or extra trainer as part
                          of the team
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-checkmark">✓</span>
                        <span className="concierge-plan-feature-text">
                          Premium support and personalized service
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-checkmark">✓</span>
                        <span className="concierge-plan-feature-text">
                          Custom training programs and interventions
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-checkmark">✓</span>
                        <span className="concierge-plan-feature-text">
                          Priority feature requests and implementation
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Upgrade Button */}
              <div className="upgrade-button-container">
                <Button
                  title={getUpgradeButtonProps().title}
                  onClick={() => handleUpgrade(selectedTier)}
                  type={ButtonType.large}
                  colorScheme={getUpgradeButtonProps().colorScheme}
                  disabled={getUpgradeButtonProps().disabled}
                  classNames="upgrade-button"
                />
              </div>
            </div>
          </div>
        </Styled>
      </DialogContent>
      <SuccessFailedModal
        open={showSuccessModal}
        onClose={handleConciergeSuccessClose}
        isError={false}
        successMessage="Thanks! Our sales team has received your request and will reach out to you shortly."
      />
    </Dialog>
  );
};

export default PaywallModal;
